import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Stack,
  TextField,
  Grid,
  DialogTitle,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { getUsers } from "../../../api/Authentication/api";
import { X } from "lucide-react";

const DukaForm = ({ initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState(
    initialData || {
      name: "",
      location: "",
      contactPerson: "",
      phoneNumber: "",
      email: "",
    }
  );

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [errors, setErrors] = useState({
    name: "",
    location: "",
    contactPerson: "",
    phoneNumber: "",
    email: "",
  });

  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoadingUsers(true);
        const userData = await getUsers();
        if (Array.isArray(userData)) {
          setUsers(userData);
          if (initialData) {
            setFormData({
              ...initialData,
            });
          }
        }
      } catch (err) {
        console.error("Error loading users:", err);
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, [initialData]);

  const validateForm = () => {
    let newErrors = {
      name: "",
      location: "",
      contactPerson: "",
      phoneNumber: "",
      email: "",
    };
    let isValid = true;
  
    if (!/^[A-Za-z][A-Za-z0-9\s]*$/.test(formData.name) || formData.name.trim() === "") {
      newErrors.name = "Enter a valid name";
      isValid = false;
    } else if (formData.name.length < 3) {
      newErrors.name = "Name should be at least 3 characters long.";
      isValid = false;
    }
  
    if (!/^[A-Za-z\s]+$/.test(formData.location) || formData.location.trim() === "") {
      newErrors.location = "Location should contain only letters and spaces.";
      isValid = false;
    }
  
    if (!/^[A-Za-z\s]+$/.test(formData.contactPerson) || formData.contactPerson.trim() === "") {
      newErrors.contactPerson = "Contact person should contain only letters and spaces.";
      isValid = false;
    } else if (formData.contactPerson.length < 3) {
      newErrors.contactPerson = "Name should be at least 3 characters long.";
      isValid = false;
    }
  
    if (!/^[+]?[0-9-]{7,15}$/.test(formData.phoneNumber) || formData.phoneNumber.trim() === "") {
      newErrors.phoneNumber = 'Phone number must be 7 to 15 digits long and may include numbers, hyphens, and an optional leading "+".';
      isValid = false;
    }
  
    if (!/^[A-Za-z][\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(formData.email) || formData.email.trim() === "") {
      newErrors.email = "Enter a valid email address.";
      isValid = false;
    }
  
    setErrors(newErrors);
    return isValid;
  };  

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <DialogTitle
        sx={{
          p: 1,
          fontSize: 18,
          fontWeight: 500,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {initialData ? "Edit Duka" : "Add New Duka"}
        <IconButton
          onClick={onCancel}
          size="small"
          sx={{ color: "text.secondary" }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
              size="medium"
              error={Boolean(errors.name)}
              helperText={errors.name}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Location"
              value={formData.location}
              onChange={(e) =>
                setFormData({ ...formData, location: e.target.value })
              }
              required
              size="medium"
              error={Boolean(errors.location)}
              helperText={errors.location}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Contact Person"
              value={formData.contactPerson}
              onChange={(e) =>
                setFormData({ ...formData, contactPerson: e.target.value })
              }
              required
              size="medium"
              error={Boolean(errors.contactPerson)}
              helperText={errors.contactPerson}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Phone Number"
              value={formData.phoneNumber}
              onChange={(e) =>
                setFormData({ ...formData, phoneNumber: e.target.value })
              }
              required
              size="medium"
              error={Boolean(errors.phoneNumber)}
              helperText={errors.phoneNumber}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
              size="medium"
              error={Boolean(errors.email)}
              helperText={errors.email}
            />
          </Grid>

          <Grid item xs={12}>
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="contained"
                onClick={onCancel}
                sx={{
                  bgcolor: "#e0e0e0",
                  color: "#000",
                  "&:hover": { bgcolor: "#d5d5d5" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                type="submit"
                disabled={loadingUsers}
                sx={{
                  bgcolor: "#00694B",
                  color: "#fff",
                  "&:hover": { bgcolor: "#005039" },
                  textTransform: "none",
                  borderRadius: 2,
                  fontWeight: 600,
                  px: 3,
                  fontSize: "16px",
                  lineHeight: "24px",
                }}
              >
                {initialData ? "Update" : "Create"}
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default DukaForm;
